<script lang="ts">
    import type { OcCinemaV1Events } from '@otto-ec/otto-components/cinema';
    import type { BlackFridaySliderAction } from '@tilelist/application/ports/TrackingPort.ts';
    import ProductTileItem from '@tilelist/components/product-tile-item/ProductTileItem.svelte';
    import type { BlackFridaySliderItem, ProductItem } from '@tilelist/domain/composition/TileListitem.ts';
    import type { TileListPageContext } from '@tilelist/domain/composition/TileListPage.ts';

    type Props = {
        slider: BlackFridaySliderItem;
        context: TileListPageContext;
        cacheRestored?: boolean;
        showRadioEquipmentRegulation?: boolean;
        headline?: string;
        subline?: string;
        onLoad: () => void;
        onAction: (action: BlackFridaySliderAction) => void;
        onProductClick: (event: Event, item: ProductItem) => void;
        onProductSeen: (item: ProductItem) => void;
    };

    let {
        slider,
        context,
        cacheRestored = true,
        showRadioEquipmentRegulation = false,
        headline = 'Black Friday Deals',
        subline = 'Die top bewerteten Produkte - bis 1.12 täglich wechselnd',
        onLoad,
        onAction,
        onProductClick,
        onProductSeen,
    }: Props = $props();

    let root = $state<HTMLElement | null>(null);
    let visibleReported = false;
    let loadRequested = false;
    let lastIndex = -1;

    /*                                                                                         */
    $effect(() => {
        if (!root || slider.state === 'collapsed') return;
        /*                                                                            */
        if (slider.state === 'pending') {
            loadRequested = false;
            visibleReported = false;
            lastIndex = -1;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                if (slider.state === 'pending') {
                    observer.disconnect();
                    if (loadRequested) return;
                    loadRequested = true;
                    onLoad();
                } else if (!visibleReported) {
                    visibleReported = true;
                    observer.disconnect();
                    onAction('visible');
                }
            },
            { rootMargin: slider.state === 'pending' ? '400px 0px' : '0px', threshold: 0 },
        );
        observer.observe(root);
        return () => observer.disconnect();
    });

    function onVisibilityChanged({ detail }: OcCinemaV1Events['oc-item-visibility-changed']) {
        const index = detail.visibleItems.indices[0] ?? 0;
        const previousIndex = lastIndex;
        lastIndex = index;
        if (previousIndex === -1 || index === previousIndex || !detail.source) return;
        onAction(index > previousIndex ? 'next' : 'previous');
    }
</script>

{#if slider.state !== 'collapsed'}
    <section
        bind:this={root}
        class="reptile-black-friday-slider"
        class:reptile-black-friday-slider--pending={slider.state === 'pending'}
        class:reptile-black-friday-slider--ready={slider.state === 'ready'}
        data-testid="black-friday-slider"
        aria-label={headline}
    >
        {#if slider.state === 'ready'}
            <header class="reptile-black-friday-slider__header">
                <h2 class="reptile-black-friday-slider__headline oc-headline-200">{headline}</h2>
                <p class="reptile-black-friday-slider__subline oc-copy-100">{subline}</p>
            </header>
            <div class="reptile-black-friday-slider__body">
                <oc-cinema-v1
                    oc-aria-label={headline}
                    resizing="none"
                    gap="8px"
                    stage-spacing="0px"
                    onoc-item-visibility-changed={onVisibilityChanged}
                >
                    {#each slider.items as item (item.product.id)}
                        <div class="reptile-black-friday-slider__tile">
                            <ProductTileItem
                                {cacheRestored}
                                productItem={item}
                                rule={context.rule}
                                layout={context.layout}
                                loadVariation={async () => {}}
                                onSeen={async () => onProductSeen(item)}
                                onTileClick={(event) => onProductClick(event, item)}
                                {showRadioEquipmentRegulation}
                                showTopInfos={false}
                                showInstallments={false}
                                showAvailability={false}
                                showComparison={false}
                                showMultipleSizes={false}
                                searchContext={context.searchContext}
                            />
                        </div>
                    {/each}
                </oc-cinema-v1>
            </div>
        {/if}
    </section>
{/if}

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';

    .reptile-black-friday-slider {
        grid-column: 1 / -1;

        /*                                                                             */
        &--ready {
            background-color: tokens.$oc-base-color-black-100;
        }

        /*                                                            */
        &--pending {
            min-height: 480px;
        }

        &__header {
            padding: 16px 16px 8px;
            color: tokens.$oc-semantic-color-text-inverted;
        }

        &__headline,
        &__subline {
            margin: 0;
        }

        &__body {
            padding: 4px 8px 8px;
            background-color: rgb(255 255 255 / 20%);
        }

        &__tile {
            overflow: hidden;

            width: 192px;
            border-radius: 4px;

            color: tokens.$oc-semantic-color-text-default;

            background-color: tokens.$oc-base-color-white-100;
        }
    }
</style>
